import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableRow, TableHead,
  Paper, Typography, Box, Button, Dialog, DialogContent, Alert, TextField, MenuItem, Grid, Card, CardContent
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import walletApi, { getPositionsByInstrumentTypeV2 } from '../../api/wallet';
import { formatValue, getInstrumentTypeLabel, getAssetClasses, getAssetInstrumentTypesByClass } from '../../utils/utils';
import OperationsForm from '../operations/OperationsForm';
import SortableTableHead from '../commons/SortableTableHead';
import useSorting from '../../utils/useSorting';
import { CHART_COLORS } from '../../constants/layout';

const assetClassOptions = [
  { value: '', label: 'Todos' },
  ...getAssetClasses(),
];

const PositionsByInstrumentTypeTable = ({ currencyView }) => {
  const [positions, setPositions] = useState([]);
  const [totalPositions, setTotalPositions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [allocationData, setAllocationData] = useState({});
  const [expandedRows, setExpandedRows] = useState([]);
  const [filters, setFilters] = useState({
    assetClass: '',
    instrumentType: '',
  });

  const columns = [
    { id: 'Asset.InstrumentType', label: 'Instrumento', sortable: true },
    { id: 'Percentage', label: '%Carteira', sortable: true },
    { id: 'TotalInvested', label: 'Total Investido', sortable: true },
    { id: 'CurrentValue', label: 'Valor Atual', sortable: true },
    { id: 'ProfitLoss', label: 'Lucro/Prejuizo', sortable: true },
    { id: 'ProfitLossPercentage', label: '%Lucro/Prejuizo', sortable: true },
    { id: 'details', label: 'Detalhes', sortable: false },
  ];

  const valueTransformers = {
    Percentage: (position) => parseFloat(position.percentage) || 0,
    TotalInvested: (position) => parseFloat(position.total_invested) || 0,
    CurrentValue: (position) => parseFloat(position.current_value) || 0,
    ProfitLoss: (position) => parseFloat(position.profit_loss) || 0,
    ProfitLossPercentage: (position) => parseFloat(position.profit_loss_percentage) || 0,
  };

  const { orderBy, order, sortedData, handleRequestSort } = useSorting(positions, 'Asset.InstrumentType', 'asc', valueTransformers);

  const fetchAllocationData = async () => {
    try {
      const response = await walletApi.get(`/Allocation/Suggestion/Distribution?CurrencyType=${currencyView}`);
      setAllocationData(response.data);
    } catch (err) {}
  };

  const fetchPositions = async (overrideFilters) => {
    const f = overrideFilters || filters;
    try {
      const response = await getPositionsByInstrumentTypeV2(currencyView, {
        AssetClass: f.assetClass || undefined,
        InstrumentType: f.instrumentType || undefined,
      });
      if (response.status === 204 || !response.data || !response.data.positions) {
        setPositions([]);
        setTotalPositions({});
        setError(null);
        return;
      }
      setPositions(Array.isArray(response.data.positions) ? response.data.positions : []);
      setTotalPositions(response.data.totals?.[0] || {});
      setError(null);
    } catch (err) {
      setError('Erro ao buscar posicoes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPositions();
    fetchAllocationData();
  }, [currencyView]);

  const toggleRowExpand = (instrumentType) => {
    setExpandedRows((prev) =>
      prev.includes(instrumentType)
        ? prev.filter((t) => t !== instrumentType)
        : [...prev, instrumentType]
    );
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setLoading(true);

    let newFilters;
    if (name === 'assetClass') {
      newFilters = { ...filters, assetClass: value, instrumentType: '' };
    } else {
      newFilters = { ...filters, [name]: value };
    }

    setFilters(newFilters);
    setTimeout(() => {
      fetchPositions(newFilters);
    }, 0);
  };

  const calculateMargin = (dataset) => {
    if (!dataset.length) {
      return { left: 40, right: 10, top: 40, bottom: 30 };
    }
    const maxLabelLength = Math.max(...dataset.map((data) => data.name.length));
    return {
      left: maxLabelLength * 12,
      right: 10,
      top: 40,
      bottom: 30,
    };
  };

  const findAllocationForInstrument = (instrumentType) => {
    const allClasses = Object.keys(allocationData?.SuggestedAllocationByInstrument || {});
    for (const assetClass of allClasses) {
      const instruments = allocationData?.SuggestedAllocationByInstrument?.[assetClass] || {};
      if (instruments[instrumentType] !== undefined) {
        return {
          assetClass,
          suggestedAllocation: instruments[instrumentType],
          adjustmentValue: allocationData?.AdjustmentValueByInstrument?.[assetClass]?.[instrumentType],
          desiredPercentage: allocationData?.DesiredPercentageByInstrument?.[assetClass]?.[instrumentType],
        };
      }
    }
    return null;
  };

  const renderBarChart = () => {
    const dataset = sortedData.map((p) => ({
      name: getInstrumentTypeLabel(p.instrument_type || p.asset_name),
      investido: parseFloat(p.total_invested || 0),
      atual: parseFloat(p.current_value || 0),
    }));

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Investimento vs Valor Atual por Instrumento ({currencyView})
        </Typography>
        <BarChart
          dataset={dataset}
          xAxis={[{ scaleType: 'band', dataKey: 'name' }]}
          series={[
            { dataKey: 'investido', label: 'Investido', color: CHART_COLORS[0] },
            { dataKey: 'atual', label: 'Atual', color: CHART_COLORS[1] },
          ]}
          height={350}
          margin={calculateMargin(dataset)}
          grid={{ horizontal: true }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select label="Classe do Ativo" name="assetClass" size="small"
                value={filters.assetClass} onChange={handleFilterChange} fullWidth
              >
                {assetClassOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select label="Instrumento" name="instrumentType" size="small"
                value={filters.instrumentType} onChange={handleFilterChange} fullWidth
                disabled={!filters.assetClass}
              >
                <MenuItem value="">Todos</MenuItem>
                {getAssetInstrumentTypesByClass(filters.assetClass).map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {!loading && !error && positions.length > 0 && renderBarChart()}

      {loading ? (
        <Typography variant="h6">Carregando...</Typography>
      ) : error ? (
        <Typography variant="h6" color="error">{error}</Typography>
      ) : positions.length === 0 ? (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            Nenhuma posicao encontrada. Cadastre uma nova operacao para acompanhar seus investimentos.
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenDialog(true)}
            >
              Nova Operacao
            </Button>
          </Box>
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
            <DialogContent>
              <OperationsForm onCreate={() => {
                fetchPositions();
                fetchAllocationData();
                setOpenDialog(false);
              }} />
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table stickyHeader>
              <SortableTableHead
                columns={columns}
                orderBy={orderBy}
                order={order}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {sortedData.map((position, index) => {
                  const percentage = position.percentage || 'N/A';
                  const totalInvested = formatValue(position.total_invested, currencyView) || 'N/A';
                  const currentValue = formatValue(position.current_value, currencyView) || 'N/A';
                  const profitLoss = formatValue(position.profit_loss, currencyView) || 'N/A';
                  const profitLossPercentage = position.profit_loss_percentage || 'N/A';
                  const instrumentType = position.instrument_type || position.asset_name;

                  const rawProfitLoss = parseFloat(position.profit_loss) || 0;
                  const rawProfitLossPercentage = parseFloat(position.profit_loss_percentage) || 0;

                  const allocationInfo = findAllocationForInstrument(instrumentType);

                  return (
                    <React.Fragment key={index}>
                      <TableRow>
                        <TableCell>{getInstrumentTypeLabel(instrumentType)}</TableCell>
                        <TableCell>{percentage}%</TableCell>
                        <TableCell>{totalInvested}</TableCell>
                        <TableCell>{currentValue}</TableCell>
                        <TableCell sx={{ color: rawProfitLoss < 0 ? 'red' : 'green' }}>
                          {profitLoss}
                        </TableCell>
                        <TableCell sx={{ color: rawProfitLossPercentage < 0 ? 'red' : 'green' }}>
                          {profitLossPercentage}%
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => toggleRowExpand(instrumentType)}
                          >
                            {expandedRows.includes(instrumentType) ? 'Esconder' : 'Detalhes'}
                          </Button>
                        </TableCell>
                      </TableRow>

                      {expandedRows.includes(instrumentType) && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Box
                              sx={{
                                p: 2,
                                bgcolor: (theme) => theme.palette.background.paper,
                                borderRadius: 2,
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                              }}
                            >
                              <Typography variant="subtitle1" gutterBottom>
                                Alocacao Sugerida para {getInstrumentTypeLabel(instrumentType)}
                              </Typography>
                              {allocationInfo ? (
                                <Table size="small" sx={{ mt: 1 }}>
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: (theme) => theme.palette.action.hover }}>
                                      <TableCell sx={{ fontWeight: 'bold' }}>Instrumento</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>Valor Sugerido</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>Valor de Ajuste</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>% Desejado</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    <TableRow>
                                      <TableCell>{getInstrumentTypeLabel(instrumentType)}</TableCell>
                                      <TableCell>{formatValue(allocationInfo.suggestedAllocation, currencyView)}</TableCell>
                                      <TableCell
                                        sx={{
                                          color: parseFloat(allocationInfo.adjustmentValue) < 0 ? 'red' : 'green',
                                          fontWeight: 'bold',
                                        }}
                                      >
                                        {formatValue(allocationInfo.adjustmentValue, currencyView)}
                                      </TableCell>
                                      <TableCell>{allocationInfo.desiredPercentage}%</TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  Nenhuma alocacao definida para este instrumento. Configure sua alocacao no Perfil.
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}

                {positions.length > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>100%</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{formatValue(totalPositions.total_invested, currencyView) || 'N/A'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{formatValue(totalPositions.current_value, currencyView) || 'N/A'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: (parseFloat(totalPositions.profit_loss) || 0) < 0 ? 'red' : 'green' }}>
                      {formatValue(totalPositions.profit_loss, currencyView) || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: (parseFloat(totalPositions.profit_loss_percentage) || 0) < 0 ? 'red' : 'green' }}>
                      {totalPositions.profit_loss_percentage || 'N/A'}%
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setOpenDialog(true)}
            >
              Nova Operacao
            </Button>
          </Box>
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
            <DialogContent>
              <OperationsForm onCreate={() => {
                fetchPositions();
                fetchAllocationData();
                setOpenDialog(false);
              }} />
            </DialogContent>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default PositionsByInstrumentTypeTable;
